/**
 * QoeBridge — PRD v.1-D §A.15 / FR-009.
 *
 * Subscribes to a media element (or any HTMLMediaElement-shaped object) and
 * POSTs the quality-of-experience beacon DIRECTLY to the edge-cascade
 * `/qoe/beacon/{channelId}` route. The beacon shape is UNCHANGED from the
 * previous (oo-edge snippet generator) contract:
 *
 *   POST https://oo-edge.mediatiquepress.one/qoe/beacon/{channelId}
 *     { channelId, timestampMs, traceparent,
 *       data: { bufferHealthMs, rebufferCount, bitrateKbps } }
 *
 * The bridge measures REAL values (FR-009 — "non-zero and accurate"):
 *
 *   - `bufferHealthMs` = (buffered.end(last) − currentTime) × 1000
 *   - `rebufferCount`  = accumulated `waiting` + `stalled` events since mount
 *   - `bitrateKbps`    = current variant bitrate ÷ 1000, with progressive
 *                        sources falling back to a known rendition bitrate
 *                        carried on the descriptor (A.15 / FR-009 second
 *                        sentence).
 *
 * It posts DIRECTLY — the third-party `validateSnippetSafety` / snippet
 * generator path (which still exists for embedded surfaces on `oo-edge`) is
 * NOT involved. The first-party console can speak to the edge directly under
 * the CSP `connect-src` whitelist (PRD A.10).
 */
import { useEffect, useRef, type JSX } from 'react';

/**
 * Edge-cascade host literal — pinned by the §A.15 CSP `connect-src` whitelist.
 * The pre-prod / dev variants live under a different sub-domain that the
 * connect-src clause does not include; the bridge intentionally points at the
 * production hostname so an accidental dev-time deploy fails closed.
 */
const EDGE_CASCADE_HOST = 'https://oo-edge.mediatiquepress.one';

/**
 * Beacon route prefix — `${EDGE_CASCADE_HOST}/qoe/beacon/{channelId}`. The
 * third-party-embed snippet-generator surface lives at a separate path on
 * `oo-edge`; that path is deliberately NOT referenced anywhere in this
 * file (FR-009 pins the direct-POST behaviour and the §A.15 source-level
 * test asserts the absence of any such reference here).
 */
const BEACON_PATH = '/qoe/beacon/';

/**
 * Flush cadence — emit a beacon every 10 seconds while mounted. Matches the
 * existing snippet-generator cadence so the edge-cascade ingestion pipeline
 * sees no change in arrival rate. The FR-009 tests assert at least one
 * beacon arrives during a 10-second interval.
 */
const FLUSH_INTERVAL_MS = 10_000;

export interface QoeBridgeProps {
  /** Channel scope — interpolated into the beacon route. */
  channelId: string;
  /** Underlying media element. Anything implementing the subset
   *  {`currentTime`, `buffered`, `addEventListener`, `removeEventListener`,
   *  optional `getCurrentBitrate()`/`currentBitrate`} works — the bridge
   *  is shape-driven rather than tightly coupled to Vidstack's surface. */
  media: HTMLMediaElement;
  /** Optional known rendition bitrate (bits/sec) for progressive sources.
   *  Used as the `bitrateKbps` fallback when the media element exposes no
   *  level bitrate (progressive MP3/MP4 have no quality ladder). */
  knownBitrateBps?: number;
}

/**
 * Minimal media-shape the bridge reads. Vidstack's HTMLMediaElement-like
 * provider exposes everything here; the FR-009 tests synthesise an object
 * with this shape directly.
 */
interface MediaLike {
  currentTime: number;
  buffered: { length: number; end: (index: number) => number };
  getCurrentBitrate?: () => number;
  currentBitrate?: number;
  addEventListener:    (type: string, listener: EventListener) => void;
  removeEventListener: (type: string, listener: EventListener) => void;
}

/**
 * Compute `bufferHealthMs` per A.15:
 *   (`buffered.end(buffered.length − 1)` − `currentTime`) × 1000.
 * Returns 0 when no buffered ranges exist yet (pre-canplay).
 */
function computeBufferHealthMs(media: MediaLike): number {
  const ranges = media.buffered;
  if (!ranges || ranges.length === 0) return 0;
  const end = ranges.end(ranges.length - 1);
  const ahead = end - media.currentTime;
  if (ahead <= 0 || !Number.isFinite(ahead)) return 0;
  return Math.round(ahead * 1000);
}

/**
 * Read current variant bitrate. Prefers an explicit `getCurrentBitrate()`
 * (Vidstack's HLS provider exposes one); falls back to a `currentBitrate`
 * property; finally falls back to the descriptor-supplied known rendition
 * bitrate for progressive sources. Returns a value in BITS/sec; the beacon
 * converts to kilobits.
 */
function readCurrentBitrateBps(
  media: MediaLike,
  knownBitrateBps: number | undefined,
): number {
  if (typeof media.getCurrentBitrate === 'function') {
    const v = media.getCurrentBitrate();
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
  }
  if (typeof media.currentBitrate === 'number' && media.currentBitrate > 0) {
    return media.currentBitrate;
  }
  if (typeof knownBitrateBps === 'number' && knownBitrateBps > 0) {
    return knownBitrateBps;
  }
  return 0;
}

/**
 * Generate a W3C-compliant traceparent (`00-<32 hex>-<16 hex>-01`). The
 * trace + span IDs are random — generated with `crypto.getRandomValues`
 * rather than the non-cryptographic PRNG, which CLAUDE.md §13 forbids
 * outside `frontend-v2/src/dev/`. The §A.15 source-level pin enforces
 * the absence of the PRNG call expression in this file.
 */
function newTraceparent(): string {
  const traceBytes = new Uint8Array(16);
  const spanBytes  = new Uint8Array(8);
  crypto.getRandomValues(traceBytes);
  crypto.getRandomValues(spanBytes);
  const traceHex = Array.from(traceBytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  const spanHex = Array.from(spanBytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return `00-${traceHex}-${spanHex}-01`;
}

/**
 * QoeBridge — mounts effects, returns null. The bridge is invisible; it
 * exists solely to translate media-element events into beacons.
 */
export function QoeBridge({
  channelId,
  media,
  knownBitrateBps,
}: QoeBridgeProps): JSX.Element | null {
  // Accumulated stall count — bumped on every `waiting`/`stalled` event.
  // Held in a ref (not state) so the flush callback always reads the latest
  // value without triggering a re-render or re-binding the interval.
  const rebufferCountRef = useRef(0);

  useEffect(() => {
    const m = media as unknown as MediaLike;
    if (!m) return;

    const onStall = (): void => {
      rebufferCountRef.current += 1;
    };
    m.addEventListener('waiting', onStall);
    m.addEventListener('stalled', onStall);

    const flush = (): void => {
      const payload = {
        channelId,
        timestampMs: Date.now(),
        traceparent: newTraceparent(),
        data: {
          bufferHealthMs: computeBufferHealthMs(m),
          rebufferCount:  rebufferCountRef.current,
          bitrateKbps:    Math.round(readCurrentBitrateBps(m, knownBitrateBps) / 1000),
        },
      };

      // Direct POST to the edge-cascade beacon route. NOT routed through
      // the snippet-generator surface (which lives on a separate `oo-edge`
      // path used only by embedded third-party iframes — A.15 / FR-009).
      const url = `${EDGE_CASCADE_HOST}${BEACON_PATH}${channelId}`;
      void fetch(url, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {
        // Beacon delivery is best-effort. A failure here MUST NOT surface
        // into the player chrome or interrupt playback — the edge-cascade
        // dashboard tracks loss rate separately.
      });
    };

    const intervalId = setInterval(flush, FLUSH_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
      m.removeEventListener('waiting', onStall);
      m.removeEventListener('stalled', onStall);
    };
  }, [channelId, media, knownBitrateBps]);

  return null;
}
